import { ShieldCheck, UserCheck, Gauge, LayoutDashboard, GraduationCap, PhoneCall, Boxes, ThumbsUp, Rocket } from "lucide-react";

const INK = "#16324F";
const CYAN_DARK = "#1D748F";
const PINK_DARK = "#D93D8F";

const SUITE = [
  {
    Icon: UserCheck,
    t: "Fractional CCO",
    b: "A named Chief Compliance Officer who owns your program — exam-ready policies, board reporting, regulator conversations. Not a hotline; a person.",
  },
  {
    Icon: ShieldCheck,
    t: "BSA/AML Officer",
    b: "Designated BSA/AML officer for your MCA flow — OFAC screening, alert triage, SAR/UAR investigation and drafting on your funding partners' calendar.",
  },
  {
    Icon: Gauge,
    t: "Risk Assessment",
    b: "A living, sales-based-financing-specific risk assessment — refreshed as your products, states, and volumes change, not a PDF that ages in a drawer.",
  },
  {
    Icon: LayoutDashboard,
    t: "Compliance Management System",
    b: "The CMS platform under it all: policies, complaints, marketing review, vendor oversight, monitoring & testing, issue tracking — one login, one audit trail.",
  },
  {
    Icon: GraduationCap,
    t: "Training Program",
    b: "The 8-module, 5-track program you've already scoped — 48-hour course turnaround, state disclosure updates included, tracking and attestations built in.",
  },
];

const PATH = [
  { Icon: PhoneCall, n: "1", t: "Discovery call", b: "Free — we learn your systems, vendor mix, and headaches." },
  { Icon: Boxes, n: "2", t: "Prototype", b: "No cost, no commitment — configured to Giggle in ~2 weeks." },
  { Icon: ThumbsUp, n: "3", t: "You decide", b: "Go live, or walk away — no obligation either way." },
  { Icon: Rocket, n: "4", t: "Go live", b: "Live with CMS and fractional team within 45 days." },
];

export function PlatformSuite() {
  return (
    <section id="suite" className="anchor-target py-14 md:py-20">
      <span className="gf-eyebrow">The full suite · what you asked to see</span>
      <h2 className="gf-h text-4xl md:text-5xl mt-3 max-w-3xl" data-testid="suite-headline">
        An outsourced compliance department — <span className="gf-grad-text">under one roof, at one price.</span>
      </h2>
      <p className="mt-5 text-lg leading-relaxed max-w-3xl" style={{ color: INK, opacity: 0.78 }}>
        Five functions most MCA shops staff separately (or skip until an exam forces the issue).
        You get all of them from one team that already knows sales-based financing — and you only
        pay for the fractional hours you actually use.
      </p>
      <div className="mt-9 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {SUITE.map(({ Icon, t, b }, i) => (
          <div key={t} className="gf-card p-6" data-testid={`suite-card-${i}`}>
            <div className="h-10 w-10 rounded-xl grid place-items-center mb-4"
                 style={{ background: `${CYAN_DARK}12` }}>
              <Icon className="h-5 w-5" style={{ color: CYAN_DARK }} strokeWidth={1.8} />
            </div>
            <div className="gf-h text-lg mb-2" style={{ color: INK }}>{t}</div>
            <p className="text-[14px] leading-relaxed" style={{ color: INK, opacity: 0.72 }}>{b}</p>
          </div>
        ))}
        <div className="gf-card p-6 flex flex-col justify-center" style={{ background: `${PINK_DARK}08`, borderColor: `${PINK_DARK}30` }}>
          <div className="gf-h text-lg mb-2" style={{ color: PINK_DARK }}>Why one roof?</div>
          <p className="text-[14px] leading-relaxed" style={{ color: INK, opacity: 0.75 }}>
            Your CCO, your BSA officer, your training, and your CMS all read from the same data.
            No hand-offs, no "that's the other vendor's module," no gaps for an examiner to find.
          </p>
        </div>
      </div>
    </section>
  );
}

export function CommitmentPath() {
  return (
    <section id="path" className="anchor-target py-14 md:py-20">
      <span className="gf-eyebrow">How to work with us</span>
      <h2 className="gf-h text-4xl md:text-5xl mt-3 max-w-3xl">
        No cost to see if this is a fit — <span className="gf-grad-text">you only pay once you go live.</span>
      </h2>
      <div className="mt-9 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {PATH.map(({ Icon, n, t, b }) => (
          <div key={n} className="gf-card p-6 relative" data-testid={`path-step-${n}`}>
            <div className="absolute top-4 right-5 gf-h text-4xl" style={{ color: `${CYAN_DARK}18` }}>{n}</div>
            <Icon className="h-5 w-5 mb-3" style={{ color: PINK_DARK }} strokeWidth={1.8} />
            <div className="gf-h text-base mb-1.5" style={{ color: INK }}>{t}</div>
            <p className="text-[13px] leading-relaxed" style={{ color: INK, opacity: 0.72 }}>{b}</p>
          </div>
        ))}
      </div>
      <div className="mt-6 gf-card p-6 md:p-7" style={{ borderColor: `${CYAN_DARK}40`, background: `${CYAN_DARK}06` }} data-testid="commitment-45">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="gf-h text-5xl flex-shrink-0" style={{ color: CYAN_DARK }}>45<span className="text-xl align-top ml-1">days</span></div>
          <div>
            <div className="gf-h text-lg" style={{ color: INK }}>The CompliSun Commitment</div>
            <p className="text-[14px] leading-relaxed mt-1" style={{ color: INK, opacity: 0.78 }}>
              Once we agree on the prototype, we commit to a fully configured, tested, and <strong>live</strong> compliance
              program — built around Giggle Finance's actual workflows — within 45 days of kickoff. If we miss that date
              for reasons within our control, <strong>your first 2 months of platform fees are on us.</strong> That's not
              a sales promise — it's a deadline we design the engagement around from day one.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
